import React, { useState, useEffect, useMemo } from 'react';
import { Help, Close, NavigateNext, NavigateBefore } from '@mui/icons-material';
import { Fab, Tooltip, Box, Typography, Button, Paper } from '@mui/material';
import './Tutorial.css';

interface TutorialStep {
  title: string;
  content: string;
  element?: string;
  highlightArea?: string; // New property for broader highlight areas
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center';
  youtubeOnly?: boolean; // New property to indicate YouTube-specific steps
  audioOnly?: boolean; // New property to indicate audio-specific steps
}

interface TutorialProps {
  isVisible?: boolean;
  onComplete?: () => void;
  isYouTube?: boolean; // New prop to indicate if we're in YouTube mode
}

const Tutorial: React.FC<TutorialProps> = ({ isVisible = true, onComplete, isYouTube = false }) => {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [tooltipPosition, setTooltipPosition] = useState({ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' });


  // Auto-start tutorial when isVisible becomes true
  useEffect(() => {
    if (isVisible && !isActive) {
      startTutorial();
    }
  }, [isVisible]);

  const tutorialSteps = useMemo(() => [
    {
      title: 'Welcome to Auditio! 🎵',
      content: 'Let\'s take a quick tour of how to create audio snippets. This tutorial will show you the key features.',
      position: 'center'
    },
    {
      title: isYouTube ? 'YouTube Video Player' : 'Waveform & Media Player',
      content: isYouTube 
        ? 'This is your YouTube video player. Use this to play/pause, seek, and control the video playback.'
        : 'This is your complete audio workspace. Use this to play/pause, seek, zoom, loop and change the playback speed.',
      element: isYouTube ? '.youtube-player-full-container' : '#waveform',
      highlightArea: isYouTube ? '.youtube-player-full-container' : '.player-container', // Highlight the entire player container and controls
      position: 'bottom',
      youtubeOnly: isYouTube,
      audioOnly: !isYouTube
    },
    {
      title: 'Start Marker',
      content: 'Click this to set the beginning of your snippet. You can also press the "S" key.',
      element: '#start-marker-btn',
      highlightArea: '#start-marker-btn', // Highlight just the button
      position: 'top'
    },
    {
      title: 'End Marker',
      content: 'Click this to set the end of your snippet. You can also press the "E" key.',
      element: '#end-marker-btn',
      highlightArea: '#end-marker-btn', // Highlight just the button
      position: 'top'
    },
    {
      title: 'Creating Snippets',
      content: isYouTube 
        ? 'To create a snippet, use the "start" and "end" buttons to create markers. A snippet will be created when you click the end button. Alternatively, create a snippet manually through the table below!'
        : 'To create a snippet, set your start and end markers. Or, drag across the waveform.',
      element: isYouTube ? '.youtube-player-container' : '#waveform',
      highlightArea: isYouTube ? '.youtube-player-container' : '#waveform',
      position: 'bottom',
      youtubeOnly: isYouTube,
      audioOnly: !isYouTube
    },
    {
      title: 'You\'re all set! 🎉',
      content: 'You now know the basics of creating audio snippets in Auditio. Try it out and have fun!',
      position: 'center'
    }
  ], [isYouTube]);

  // Filter steps based on whether we're in YouTube or audio mode
  const filteredSteps = useMemo(() => {
    return tutorialSteps.filter(step => {
      if (step.youtubeOnly && !isYouTube) return false;
      if (step.audioOnly && isYouTube) return false;
      return true;
    });
  }, [isYouTube]);

  const startTutorial = () => {
    setIsActive(true);
    setCurrentStep(0);
  };

  const nextStep = () => {
    if (currentStep < filteredSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeTutorial();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const completeTutorial = () => {
    setIsActive(false);
    if (onComplete) {
      onComplete();
    }
  };

  const skipTutorial = () => {
    setIsActive(false);
    if (onComplete) {
      onComplete();
    }
  };

  // Calculate tooltip position based on target element
  const calculateTooltipPosition = (targetElement: HTMLElement | null, position: string) => {
    // If no target element, return center position
    if (!targetElement) {
      console.log('Tutorial: No target element provided, using center position');
      return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    }
    
    try {
      const rect = targetElement.getBoundingClientRect();
      console.log('Tutorial: Element rect:', rect);
      const tooltipWidth = Math.min(400, window.innerWidth - 40); // Responsive width
      const tooltipHeight = 200;
      const margin = 20;
      const isMobile = window.innerWidth <= 768;

      let top = '50%';
      let left = '50%';
      let transform = 'translate(-50%, -50%)';

      // Special handling for waveform and media player steps to position tooltip outside the overlay
      if (currentStep === 1) { // Waveform & Media Player step
        // Position tooltip at the top of the screen, above the waveform
        top = `${margin}px`;
        left = '50%';
        transform = 'translate(-50%, 0)';
        return { top, left, transform };
      }

      // Special handling for Creating Snippets step to position tooltip outside the overlay
      if (currentStep === 4) { // Creating Snippets step
        // Position tooltip at the top of the screen, above the waveform
        top = `${margin}px`;
        left = '50%';
        transform = 'translate(-50%, 0)';
        return { top, left, transform };
      }

      // Adjust positioning for mobile
      if (isMobile) {
        // On mobile, prefer bottom positioning to avoid covering top UI elements
        if (position === 'top' || position === 'left' || position === 'right') {
          position = 'bottom';
        }
      }

      switch (position) {
        case 'top':
          top = `${rect.top - tooltipHeight - margin}px`;
          left = `${rect.left + rect.width / 2}px`;
          transform = 'translate(-50%, -100%)';
          break;
        case 'bottom':
          top = `${rect.bottom + margin}px`;
          left = `${rect.left + rect.width / 2}px`;
          transform = 'translate(-50%, 0)';
          break;
        case 'left':
          top = `${rect.top + rect.height / 2}px`;
          left = `${rect.left - tooltipWidth - margin}px`;
          transform = 'translate(-100%, -50%)';
          break;
        case 'right':
          top = `${rect.top + rect.height / 2}px`;
          left = `${rect.right + margin}px`;
          transform = 'translate(0, -50%)';
          break;
        default:
          // Center position
          top = '50%';
          left = '50%';
          transform = 'translate(-50%, -50%)';
      }

      // Ensure tooltip stays within viewport
      const tooltipRect = {
        width: tooltipWidth,
        height: tooltipHeight,
        top: position === 'top' ? rect.top - tooltipHeight - margin : 
             position === 'bottom' ? rect.bottom + margin : 
             position === 'left' ? rect.top + rect.height / 2 - tooltipHeight / 2 : 
             position === 'right' ? rect.top + rect.height / 2 - tooltipHeight / 2 : 
             window.innerHeight / 2 - tooltipHeight / 2,
        left: position === 'left' ? rect.left - tooltipWidth - margin : 
              position === 'right' ? rect.right + margin : 
              position === 'top' || position === 'bottom' ? rect.left + rect.width / 2 - tooltipWidth / 2 : 
              window.innerWidth / 2 - tooltipWidth / 2
      };

      // Adjust if tooltip goes off screen
      if (tooltipRect.left < margin) {
        left = `${margin}px`;
        transform = position === 'left' ? 'translate(0, -50%)' : transform;
      } else if (tooltipRect.left + tooltipRect.width > window.innerWidth - margin) {
        left = `${window.innerWidth - tooltipRect.width - margin}px`;
        transform = position === 'right' ? 'translate(-100%, -50%)' : transform;
      }

      if (tooltipRect.top < margin) {
        top = `${margin}px`;
        transform = position === 'top' ? 'translate(-50%, 0)' : transform;
      } else if (tooltipRect.top + tooltipRect.height > window.innerHeight - margin) {
        top = `${window.innerHeight - tooltipRect.height - margin}px`;
        transform = position === 'bottom' ? 'translate(-50%, -100%)' : transform;
      }

      // Special mobile adjustments
      if (isMobile) {
        // Ensure tooltip doesn't cover the bottom area where zoom buttons might be
        const bottomSafeArea = 120; // Increased safe area for loop controls and zoom buttons
        
        // Check if we're near the bottom of the screen
        if (tooltipRect.top + tooltipRect.height > window.innerHeight - bottomSafeArea) {
          // Try to position above the element instead
          const newTop = rect.top - tooltipHeight - margin;
          if (newTop > margin) {
            top = `${newTop}px`;
            transform = 'translate(-50%, -100%)';
          } else {
            // If above doesn't work, position at the top of the screen
            top = `${margin}px`;
            transform = 'translate(-50%, 0)';
          }
        }
        
        // Center horizontally on mobile for better readability
        if (position === 'top' || position === 'bottom') {
          left = '50%';
          // Keep the transform from the positioning logic above
        }
        
        // Special handling for loop controls - always position above on mobile
        if (currentStep === 7) { // Loop options step
          const newTop = rect.top - tooltipHeight - margin;
          if (newTop > margin) {
            top = `${newTop}px`;
            transform = 'translate(-50%, -100%)';
          } else {
            top = `${margin}px`;
            transform = 'translate(-50%, 0)';
          }
          // Always center horizontally for loop options on mobile
          left = '50%';
          // Override any previous transform to ensure proper centering
          if (newTop > margin) {
            transform = 'translate(-50%, -100%)';
          } else {
            transform = 'translate(-50%, 0)';
          }
        }
      }

      return { top, left, transform };
    } catch (error) {
      console.error('Tutorial: Error calculating tooltip position:', error);
      return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    }
  };

  // Highlight the current element and create targeted overlay
  useEffect(() => {
    if (isActive && filteredSteps[currentStep].element) {
      // Function to find elements with retry logic
      const findElementWithRetry = (selector: string, maxRetries = 10, delay = 100): Promise<Element | null> => {
        return new Promise((resolve) => {
          let attempts = 0;
          
          const tryFind = () => {
            const element = document.querySelector(selector);
            if (element) {
              resolve(element);
            } else if (attempts < maxRetries) {
              attempts++;
              setTimeout(tryFind, delay);
            } else {
              console.warn(`Tutorial: Element not found after ${maxRetries} attempts: ${selector}`);
              resolve(null);
            }
          };
          
          tryFind();
        });
      };

      // Function to create overlay with retry logic
      const createOverlayWithRetry = async () => {
        try {
          console.log('Tutorial: Looking for element:', filteredSteps[currentStep].element);
          const element = await findElementWithRetry(filteredSteps[currentStep].element!);
          let highlightArea: Element | null = null;
          
          if (filteredSteps[currentStep].highlightArea) {
            console.log('Tutorial: Looking for highlight area:', filteredSteps[currentStep].highlightArea);
            highlightArea = await findElementWithRetry(filteredSteps[currentStep].highlightArea!);
          } else {
            highlightArea = element;
          }
          
          // Special handling for time jump buttons - highlight all buttons
          if (currentStep === 3 && filteredSteps[currentStep].highlightArea === '.time-jump-btn') {
            const buttons = document.querySelectorAll('.time-jump-btn');
            if (buttons.length > 0) {
              highlightArea = createMultiElementHighlightArea(buttons);
            }
          }
          
          // Special handling for loop options - highlight multiple elements
          if (currentStep === 7 && filteredSteps[currentStep].highlightArea === '.loop-label, #loop-selector') {
            const loopLabel = await findElementWithRetry('.loop-label');
            const loopSelector = await findElementWithRetry('#loop-selector');
            if (loopLabel && loopSelector) {
              highlightArea = createMultiElementHighlightArea([loopLabel, loopSelector]);
            }
          }
          
          if (element) {
            console.log('Tutorial: Element found, creating overlay');
            // Create targeted overlay around the highlight area (broader area)
            if (highlightArea) {
              // Add a delay to ensure layout is stable
              setTimeout(() => {
                createTargetedOverlay(highlightArea as HTMLElement);
              }, 100);
            }
            
            // Calculate tooltip position based on the original element
            const position = calculateTooltipPosition(element as HTMLElement, filteredSteps[currentStep].position || 'center');
            setTooltipPosition(position);
          } else {
            console.log('Tutorial: Element not found, using center position');
            // If element not found, use center position
            setTooltipPosition({ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' });
          }
        } catch (error) {
          console.error('Tutorial: Error in createOverlayWithRetry:', error);
          // Fallback to center position
          setTooltipPosition({ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' });
        }
      };

      createOverlayWithRetry();
    } else if (isActive && filteredSteps[currentStep].position === 'center') {
      // Center position for steps without specific elements
      setTooltipPosition({ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' });
    }

    return () => {
      // Remove overlay and any custom highlight elements when component unmounts or step changes
      document.querySelectorAll('.tutorial-overlay').forEach(el => {
        el.remove();
      });
      document.querySelectorAll('.tutorial-multi-highlight').forEach(el => {
        el.remove();
      });
    };
  }, [isActive, currentStep, isYouTube]);

  // Create a highlight area that encompasses multiple elements
  const createMultiElementHighlightArea = (elements: NodeListOf<Element> | Element[]): HTMLElement => {
    // Remove any existing custom highlight elements
    document.querySelectorAll('.tutorial-multi-highlight').forEach(el => el.remove());

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

    // Calculate the bounding box that encompasses all elements
    elements.forEach((element) => {
      const rect = element.getBoundingClientRect();
      minX = Math.min(minX, rect.left);
      minY = Math.min(minY, rect.top);
      maxX = Math.max(maxX, rect.right);
      maxY = Math.max(maxY, rect.bottom);
    });

    // Create a temporary div to represent the highlight area
    const highlightDiv = document.createElement('div');
    highlightDiv.className = 'tutorial-multi-highlight';
    highlightDiv.style.cssText = `
      position: fixed;
      top: ${minY}px;
      left: ${minX}px;
      width: ${maxX - minX}px;
      height: ${maxY - minY}px;
      pointer-events: none;
      z-index: -1;
    `;

    document.body.appendChild(highlightDiv);
    return highlightDiv;
  };

  // Disable scrolling during tutorial
  useEffect(() => {
    if (isActive) {
      // Disable scrolling without affecting layout
      document.body.style.overflow = 'hidden';
    } else {
      // Re-enable scrolling
      document.body.style.overflow = '';
    }

    return () => {
      // Cleanup: re-enable scrolling if component unmounts while tutorial is active
      if (isActive) {
        document.body.style.overflow = '';
      }
    };
  }, [isActive]);

  

  const createTargetedOverlay = (targetElement: HTMLElement) => {
    // Remove any existing overlays
    document.querySelectorAll('.tutorial-overlay').forEach(el => el.remove());

    const rect = targetElement.getBoundingClientRect();
    
    // Check if element has valid dimensions
    if (rect.width === 0 || rect.height === 0) {
      console.warn('Tutorial: Target element has no dimensions, skipping overlay');
      return;
    }
    
    const padding = 12; // Slightly more padding for broader areas

    // Create overlay container
    const overlay = document.createElement('div');
    overlay.className = 'tutorial-overlay';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 9998;
    `;

    // Create SVG mask to cut out the target element
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', '100%');
    svg.setAttribute('height', '100%');
    svg.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      pointer-events: none;
    `;

    // Create mask
    const mask = document.createElementNS('http://www.w3.org/2000/svg', 'mask');
    mask.setAttribute('id', `tutorial-mask-${Date.now()}`);

    // Background rectangle (dark overlay)
    const bgRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    bgRect.setAttribute('width', '100%');
    bgRect.setAttribute('height', '100%');
    bgRect.setAttribute('fill', 'white');

    // Cut-out rectangle (transparent area around target)
    const cutoutRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    cutoutRect.setAttribute('x', (rect.left - padding).toString());
    cutoutRect.setAttribute('y', (rect.top - padding).toString());
    cutoutRect.setAttribute('width', (rect.width + padding * 2).toString());
    cutoutRect.setAttribute('height', (rect.height + padding * 2).toString());
    cutoutRect.setAttribute('fill', 'black');
    cutoutRect.setAttribute('rx', '12'); // Rounded corners for broader areas

    mask.appendChild(bgRect);
    mask.appendChild(cutoutRect);
    svg.appendChild(mask);

    // Dark overlay rectangle
    const overlayRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    overlayRect.setAttribute('width', '100%');
    overlayRect.setAttribute('height', '100%');
    overlayRect.setAttribute('fill', 'rgba(0, 0, 0, 0.75)');
    overlayRect.setAttribute('mask', `url(#tutorial-mask-${Date.now()})`);

    svg.appendChild(overlayRect);
    overlay.appendChild(svg);
    document.body.appendChild(overlay);
  };

  if (!isVisible) {
    return null;
  }

  if (!isActive) {
    return null;
  }

  const currentStepData = filteredSteps[currentStep];

  return (
    <>
      {/* Backdrop to close tutorial when clicking outside */}
      <div
        onClick={skipTutorial}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 9998,
          background: 'transparent',
        }}
      />
      
      
      
      {/* Tutorial Tooltip */}
      <Paper
        elevation={24}
        onClick={(e) => e.stopPropagation()}
        sx={{
          position: 'fixed',
          top: tooltipPosition.top,
          left: tooltipPosition.left,
          transform: tooltipPosition.transform,
          maxWidth: { xs: 'calc(100vw - 40px)', sm: 450 },
          width: { xs: 'calc(100vw - 40px)', sm: 'auto' },
          padding: { xs: 2, sm: 3 },
          zIndex: 9999,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'white', fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
            {currentStepData.title}
          </Typography>
          <Button
            onClick={skipTutorial}
            sx={{ minWidth: 'auto', p: 0.5, color: 'rgba(255, 255, 255, 0.7)' }}
          >
            <Close />
          </Button>
        </Box>

        <Typography variant="body1" sx={{ 
          mb: 3, 
          lineHeight: 1.6, 
          color: 'rgba(255, 255, 255, 0.9)',
          fontSize: { xs: '0.9rem', sm: '1rem' }
        }}>
          {currentStepData.content}
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            {currentStep + 1} of {filteredSteps.length}
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
            {currentStep > 0 && (
              <Button
                onClick={prevStep}
                variant="outlined"
                size="small"
                sx={{
                  color: 'white',
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  fontSize: { xs: '0.75rem', sm: '0.8rem' },
                  minWidth: 'fit-content',
                  whiteSpace: 'nowrap',
                  height: '36px',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontWeight: 500,
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  }
                }}
              >
                <NavigateBefore sx={{ fontSize: '16px', mr: 0.5 }} />
                Previous
              </Button>
            )}
            
            <Button
              onClick={nextStep}
              variant="contained"
              size="small"
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                fontSize: { xs: '0.75rem', sm: '0.8rem' },
                minWidth: 'fit-content',
                whiteSpace: 'nowrap',
                height: '36px',
                padding: '8px 16px',
                borderRadius: '6px',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.3)',
                }
              }}
            >
              {currentStep === filteredSteps.length - 1 ? 'Finish' : 'Next'}
              <NavigateNext sx={{ fontSize: '16px', ml: 0.5 }} />
            </Button>
          </Box>
        </Box>
      </Paper>
    </>
  );
};

export default Tutorial; 